import { ptPT, type TranslationKey, type Translations } from './locales/pt-PT.js';
import { en } from './locales/en.js';

export type { TranslationKey, Translations } from './locales/pt-PT.js';

export type Locale = 'pt-PT' | 'en';
export const SUPPORTED_LOCALES: readonly Locale[] = Object.freeze(['pt-PT', 'en']);
export const DEFAULT_LOCALE: Locale = 'pt-PT';

const TABLES: Readonly<Record<Locale, Translations>> = Object.freeze({
  'pt-PT': ptPT,
  en,
});

const STORAGE_KEY = 'naintufaiv.locale';
const LEGACY_STORAGE_KEY = 'ninetofive.locale';

function detectFromNavigator(): Locale {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE;
  const lang = navigator.language ?? '';
  if (lang.toLowerCase().startsWith('pt')) return 'pt-PT';
  return 'en';
}

function readStoredLocale(): Locale | null {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    let v = ls.getItem(STORAGE_KEY);
    if (v === null) {
      const legacy = ls.getItem(LEGACY_STORAGE_KEY);
      if (legacy !== null) {
        ls.setItem(STORAGE_KEY, legacy);
        ls.removeItem(LEGACY_STORAGE_KEY);
        v = legacy;
      }
    }
    return v === 'pt-PT' || v === 'en' ? v : null;
  } catch {
    return null;
  }
}

let currentLocale: Locale = readStoredLocale() ?? detectFromNavigator();

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(locale: Locale): void {
  currentLocale = locale;
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore (no storage available)
  }
}

export function t(key: TranslationKey, params?: Record<string, string | number>): string {
  const template = TABLES[currentLocale][key];
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, k: string) =>
    params[k] !== undefined ? String(params[k]) : `{${k}}`,
  );
}

export function formatCurrency(cents: number): string {
  return new Intl.NumberFormat(currentLocale, {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

export function plural(
  count: number,
  oneKey: TranslationKey,
  otherKey: TranslationKey,
  extraParams?: Record<string, string | number>,
): string {
  const pr = new Intl.PluralRules(currentLocale);
  const key = pr.select(count) === 'one' ? oneKey : otherKey;
  return t(key, { count, ...extraParams });
}
